/**
 * Portfolio API Client
 * Handles portfolio overview, positions, performance, and allocation
 */

import { BaseApiClient } from './base-client';
import { ApiResponse } from './config';

// Portfolio Types
export interface Position {
  symbol: string;
  quantity: number;
  average_price: number;
  current_price: number;
  market_value: number;
  unrealized_pnl: number;
  unrealized_pnl_percent: number;
  day_change: number;
  day_change_percent: number;
  cost_basis: number;
  weight: number; // percentage of portfolio
  last_updated: string;
}

export interface PortfolioOverview {
  user_id: string;
  total_value: number;
  cash_balance: number;
  invested_value: number;
  total_pnl: number;
  total_pnl_percent: number;
  day_pnl: number;
  day_pnl_percent: number;
  positions_count: number;
  diversity_score: number; // 0-100
  risk_score: number; // 0-100
  last_updated: string;
}

export interface PerformanceMetrics {
  user_id: string;
  period: string;
  total_return: number;
  total_return_percent: number;
  annualized_return: number;
  volatility: number;
  sharpe_ratio: number;
  max_drawdown: number;
  max_drawdown_percent: number;
  win_rate: number;
  profit_factor: number;
  sortino_ratio: number;
  calmar_ratio: number;
  beta: number;
  alpha: number;
  tracking_error: number;
  information_ratio: number;
}

export interface AllocationData {
  allocations: Array<{
    category: string;
    value: number;
    percentage: number;
    target_percentage?: number;
    deviation?: number;
  }>;
  allocation_type: 'sector' | 'asset_class' | 'geographic' | 'market_cap';
  rebalancing_needed: boolean;
  rebalancing_suggestions?: Array<{
    symbol: string;
    action: 'buy' | 'sell' | 'hold';
    current_weight: number;
    target_weight: number;
    suggested_quantity: number;
  }>;
}

export interface PortfolioHistory {
  date: string;
  total_value: number;
  cash_balance: number;
  invested_value: number;
  total_pnl: number;
  day_pnl: number;
}

export interface Dividend {
  symbol: string;
  amount: number;
  currency: string;
  ex_date: string;
  pay_date: string;
  record_date: string;
  declaration_date: string;
  frequency: 'monthly' | 'quarterly' | 'semi-annual' | 'annual';
  yield_percent: number;
}

export interface Trade {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
  price: number;
  total_value: number;
  fees: number;
  net_value: number;
  executed_at: string;
  order_id?: string;
  notes?: string;
}

export interface PortfolioAnalysis {
  correlation_matrix: Record<string, Record<string, number>>;
  sector_exposure: Record<string, number>;
  country_exposure: Record<string, number>;
  currency_exposure: Record<string, number>;
  concentration_risk: {
    top_5_positions_weight: number;
    top_10_positions_weight: number;
    herfindahl_index: number;
  };
  risk_metrics: {
    portfolio_beta: number;
    portfolio_volatility: number;
    var_1d: number;
    var_5d: number;
    expected_shortfall: number;
  };
}

export interface RebalancingRequest {
  target_allocations: Array<{
    symbol: string;
    target_weight: number;
  }>;
  rebalancing_method: 'threshold' | 'calendar' | 'tactical';
  threshold_percent?: number;
  min_trade_amount?: number;
  exclude_symbols?: string[];
}

export class PortfolioApiClient extends BaseApiClient {
  /**
   * Get portfolio overview
   */
  async getPortfolioOverview(): Promise<ApiResponse<PortfolioOverview>> {
    return this.get('/api/v1/portfolio/overview');
  }

  /**
   * Get all positions
   */
  async getPositions(includeZero: boolean = false): Promise<ApiResponse<Position[]>> {
    return this.get(`/api/v1/portfolio/positions?include_zero=${includeZero}`);
  }

  /**
   * Get specific position
   */
  async getPosition(symbol: string): Promise<ApiResponse<Position>> {
    return this.get(`/api/v1/portfolio/positions/${symbol}`);
  }

  /**
   * Get portfolio performance metrics
   */
  async getPerformance(period: string = '1Y'): Promise<ApiResponse<PerformanceMetrics>> {
    return this.get(`/api/v1/portfolio/performance?period=${period}`);
  }

  /**
   * Get portfolio allocation breakdown
   */
  async getAllocation(type: 'sector' | 'asset_class' | 'geographic' | 'market_cap' = 'sector'): Promise<ApiResponse<AllocationData>> {
    return this.get(`/api/v1/portfolio/allocation?type=${type}`);
  }

  /**
   * Get portfolio historical data
   */
  async getPortfolioHistory(
    startDate: string,
    endDate: string,
    interval: 'daily' | 'weekly' | 'monthly' = 'daily'
  ): Promise<ApiResponse<PortfolioHistory[]>> {
    return this.get(`/api/v1/portfolio/history?start_date=${startDate}&end_date=${endDate}&interval=${interval}`);
  }

  /**
   * Get dividend information
   */
  async getDividends(
    startDate?: string,
    endDate?: string,
    symbol?: string
  ): Promise<ApiResponse<Dividend[]>> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (symbol) params.append('symbol', symbol);

    const queryString = params.toString();
    return this.get(`/api/v1/portfolio/dividends${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get trade history
   */
  async getTradeHistory(
    limit: number = 50,
    offset: number = 0,
    symbol?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<{
    trades: Trade[];
    total: number;
    has_more: boolean;
  }>> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (symbol) params.append('symbol', symbol);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return this.get(`/api/v1/portfolio/trades?${params.toString()}`);
  }

  /**
   * Get portfolio analysis
   */
  async getPortfolioAnalysis(): Promise<ApiResponse<PortfolioAnalysis>> {
    return this.get('/api/v1/portfolio/analysis');
  }

  /**
   * Calculate optimal portfolio allocation
   */
  async calculateOptimalAllocation(
    method: 'mean_variance' | 'risk_parity' | 'equal_weight' | 'market_cap',
    constraints?: {
      max_weight?: number;
      min_weight?: number;
      target_return?: number;
      target_volatility?: number;
    }
  ): Promise<ApiResponse<{
    allocations: Array<{
      symbol: string;
      current_weight: number;
      optimal_weight: number;
      weight_change: number;
    }>;
    expected_return: number;
    expected_volatility: number;
    sharpe_ratio: number;
  }>> {
    return this.post('/api/v1/portfolio/optimization', {
      method,
      constraints: constraints || {}
    });
  }

  /**
   * Generate rebalancing suggestions
   */
  async getRebalancingSuggestions(data: RebalancingRequest): Promise<ApiResponse<{
    suggestions: Array<{
      symbol: string;
      action: 'buy' | 'sell';
      quantity: number;
      estimated_cost: number;
      current_weight: number;
      target_weight: number;
    }>;
    total_trades: number;
    estimated_fees: number;
    net_cash_required: number;
  }>> {
    return this.post('/api/v1/portfolio/rebalancing', data);
  }

  /**
   * Execute rebalancing
   */
  async executeRebalancing(rebalancingId: string): Promise<ApiResponse<{
    rebalancing_id: string;
    status: 'pending' | 'executing' | 'completed' | 'failed';
    orders_placed: number;
    estimated_completion: string;
  }>> {
    return this.post(`/api/v1/portfolio/rebalancing/${rebalancingId}/execute`);
  }

  /**
   * Get portfolio benchmark comparison
   */
  async getBenchmarkComparison(
    benchmark: string = 'SPY',
    period: string = '1Y'
  ): Promise<ApiResponse<{
    portfolio_return: number;
    benchmark_return: number;
    outperformance: number;
    tracking_error: number;
    beta: number;
    alpha: number;
    correlation: number;
    information_ratio: number;
    historical_data: Array<{
      date: string;
      portfolio_value: number;
      benchmark_value: number;
    }>;
  }>> {
    return this.get(`/api/v1/portfolio/benchmark?benchmark=${benchmark}&period=${period}`);
  }

  /**
   * Get portfolio risk metrics
   */
  async getRiskMetrics(): Promise<ApiResponse<{
    var_1d: number;
    var_5d: number;
    expected_shortfall: number;
    beta: number;
    volatility: number;
    max_drawdown: number;
    concentration_risk: number;
    correlation_risk: number;
    currency_risk: number;
  }>> {
    return this.get('/api/v1/portfolio/risk-metrics');
  }

  /**
   * Export portfolio data
   */
  async exportPortfolioData(
    format: 'csv' | 'xlsx' | 'pdf' = 'csv',
    includeHistory: boolean = false
  ): Promise<ApiResponse<{
    download_url: string;
    expires_at: string;
    file_size: number;
  }>> {
    return this.post('/api/v1/portfolio/export', {
      format,
      include_history: includeHistory
    });
  }

  /**
   * Close a position (full or partial)
   */
  async closePosition(positionId: string, quantity?: number): Promise<ApiResponse<{
    order_id: string;
    symbol: string;
    quantity_closed: number;
    estimated_proceeds: number;
  }>> {
    return this.post(`/api/v1/portfolio/positions/${positionId}/close`, {
      quantity: quantity || undefined
    });
  }

  /**
   * Update portfolio allocation
   */
  async updateAllocation(portfolioId: string | undefined, allocation: any[]): Promise<ApiResponse<{
    updated_allocations: Array<{
      symbol: string;
      old_weight: number;
      new_weight: number;
    }>;
    rebalancing_required: boolean;
  }>> {
    return this.post(`/api/v1/portfolio/allocation`, {
      portfolio_id: portfolioId,
      allocations: allocation
    });
  }

  /**
   * Get detailed portfolio analytics
   */
  async getAnalytics(portfolioId?: string, timePeriod?: string): Promise<ApiResponse<{
    performance_metrics: PerformanceMetrics;
    risk_metrics: any;
    allocation_analysis: AllocationData;
    correlation_matrix: Record<string, Record<string, number>>;
  }>> {
    const params = new URLSearchParams();
    if (portfolioId) params.append('portfolio_id', portfolioId);
    if (timePeriod) params.append('period', timePeriod);

    const queryString = params.toString();
    return this.get(`/api/v1/portfolio/analytics${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get correlation analysis between portfolio positions
   */
  async getCorrelationAnalysis(portfolioId?: string, timePeriod?: string): Promise<ApiResponse<{
    correlation_matrix: Record<string, Record<string, number>>;
    correlation_clusters: Array<{
      symbols: string[];
      average_correlation: number;
    }>;
    diversification_score: number;
  }>> {
    const params = new URLSearchParams();
    if (portfolioId) params.append('portfolio_id', portfolioId);
    if (timePeriod) params.append('period', timePeriod);

    const queryString = params.toString();
    return this.get(`/api/v1/portfolio/correlation${queryString ? `?${queryString}` : ''}`);
  }
}

